import { useSyncExternalStore } from 'react';
import { stripFollowups } from '@/components/assistant-ui/followup-markers';
import type { PresentChartArgs } from '../ChartToolUI';

export type BackgroundReport = {
  summary: string;
  chart?: PresentChartArgs | null;
};

const PLACEHOLDER =
  'گزارش سرعت در حال ساخت است. نتیجه در همین گفتگو می‌آید.';

let reports = new Map<string, BackgroundReport>();
const listeners = new Set<() => void>();

export function publishBackgroundReport(
  channelId: string,
  report: BackgroundReport
): void {
  const next = new Map(reports);
  next.set(channelId, report);
  reports = next;
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useBackgroundReports(): Map<string, BackgroundReport> {
  return useSyncExternalStore(
    subscribe,
    () => reports,
    () => reports
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function parseToolResult(result: unknown): Record<string, unknown> | null {
  if (typeof result === 'string') {
    const start = result.indexOf('{');
    const end = result.lastIndexOf('}');
    if (start < 0 || end <= start) return null;
    try {
      return asRecord(JSON.parse(result.slice(start, end + 1)));
    } catch {
      return null;
    }
  }
  return asRecord(result);
}

export function backgroundAnswerText(
  content: unknown,
  live: Map<string, BackgroundReport>
): string {
  const chunks: string[] = [];
  const parts = Array.isArray(content) ? content : [];
  for (const part of parts) {
    const row = asRecord(part);
    if (!row || row.type !== 'tool-call') continue;
    if (row.toolName !== 'lighthouse_create') continue;
    const parsed = parseToolResult(row.result);
    const channelId =
      typeof parsed?.channelId === 'string' ? parsed.channelId : '';
    const stored = channelId ? live.get(channelId)?.summary : '';
    const summary =
      stored ||
      (typeof parsed?.summary === 'string' ? parsed.summary : '');
    const text = stripFollowups(summary).trim();
    if (text) chunks.push(text);
  }
  return chunks.join('\n\n');
}

export function isSpeedPlaceholder(text: string): boolean {
  const trimmed = text.trim();
  return (
    !trimmed ||
    trimmed === PLACEHOLDER ||
    trimmed === 'گزارش سرعت آماده است.'
  );
}
